/**
 * Utility functions for candidate management
 */

// Calculate skills match percentage between candidate and job
export const calculateSkillsMatch = (
  candidateSkills: string[],
  requiredSkills: string[]
): number => {
  if (!candidateSkills || candidateSkills.length === 0) return 0;
  if (!requiredSkills || requiredSkills.length === 0) return 0;

  const normalizedCandidateSkills = candidateSkills.map((s) =>
    s.toLowerCase().trim()
  );
  const normalizedRequiredSkills = requiredSkills.map((s) =>
    s.toLowerCase().trim()
  );

  const matchedSkills = normalizedRequiredSkills.filter((required) =>
    normalizedCandidateSkills.some((candidate) =>
      candidate.includes(required) || required.includes(candidate)
    )
  );

  const matchPercentage = (matchedSkills.length / normalizedRequiredSkills.length) * 100;
  return Math.round(matchPercentage);
};

// Extract skills from job description
export const extractSkillsFromJD = (jobDescription: string): string[] => {
  // Common programming languages and technologies
  const commonSkills = [
    'JavaScript', 'TypeScript', 'Python', 'Java', 'C++', 'C#', 'Ruby', 'PHP', 'Swift', 'Kotlin',
    'React', 'Angular', 'Vue', 'Node.js', 'Express', 'Django', 'Flask', 'Spring', 'Laravel',
    'HTML', 'CSS', 'SCSS', 'Tailwind', 'Bootstrap', 'Material-UI',
    'SQL', 'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Firebase', 'Firestore',
    'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'CI/CD', 'Git', 'Jenkins',
    'REST', 'GraphQL', 'API', 'Microservices', 'Agile', 'Scrum',
    'Machine Learning', 'AI', 'Data Science', 'TensorFlow', 'PyTorch',
  ];

  const foundSkills: string[] = [];
  const lowerJD = jobDescription.toLowerCase();

  commonSkills.forEach((skill) => {
    if (lowerJD.includes(skill.toLowerCase())) {
      foundSkills.push(skill);
    }
  });

  return foundSkills;
};

// Format timeline for candidate journey
export const formatTimeline = (candidate: any): Array<{ event: string; date: string; daysAgo: number }> => {
  const timeline: Array<{ event: string; date: string; daysAgo: number }> = [];

  const now = new Date();

  // Test completed
  if (candidate.completedAt) {
    const completedDate = candidate.completedAt.toDate ? candidate.completedAt.toDate() : new Date(candidate.completedAt);
    const daysAgo = Math.floor((now.getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24));
    timeline.push({
      event: 'Test completed',
      date: completedDate.toLocaleDateString(),
      daysAgo,
    });
  }

  // Shortlisted
  if (candidate.shortlistedAt) {
    const shortlistedDate = candidate.shortlistedAt.toDate ? candidate.shortlistedAt.toDate() : new Date(candidate.shortlistedAt);
    const daysAgo = Math.floor((now.getTime() - shortlistedDate.getTime()) / (1000 * 60 * 60 * 24));
    timeline.push({
      event: 'Shortlisted',
      date: shortlistedDate.toLocaleDateString(),
      daysAgo,
    });
  }

  // Rejected
  if (candidate.rejectedAt) {
    const rejectedDate = candidate.rejectedAt.toDate ? candidate.rejectedAt.toDate() : new Date(candidate.rejectedAt);
    const daysAgo = Math.floor((now.getTime() - rejectedDate.getTime()) / (1000 * 60 * 60 * 24));
    timeline.push({
      event: 'Rejected',
      date: rejectedDate.toLocaleDateString(),
      daysAgo,
    });
  }

  return timeline.sort((a, b) => b.daysAgo - a.daysAgo);
};

// Format days ago text
export const formatDaysAgo = (daysAgo: number): string => {
  if (daysAgo === 0) return 'today';
  if (daysAgo === 1) return '1 day ago';
  if (daysAgo < 7) return `${daysAgo} days ago`;
  if (daysAgo < 30) {
    const weeks = Math.floor(daysAgo / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  const months = Math.floor(daysAgo / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
};

// Export candidates to CSV
export const exportToCSV = (candidates: any[], filename: string = 'candidates.csv'): void => {
  if (candidates.length === 0) {
    alert('No candidates to export');
    return;
  }

  // Define CSV headers
  const headers = [
    'Name',
    'Email',
    'Position',
    'MCQ Score',
    'Interview Score',
    'Total Score',
    'Recommendation',
    'Skills',
    'Experience',
    'Location',
    'Applied Date',
    'Status',
  ];

  // Convert candidates to CSV rows
  const rows = candidates.map((candidate) => [
    candidate.name || '',
    candidate.email || '',
    candidate.position || '',
    candidate.mcqScore?.toFixed(1) || '0',
    candidate.interviewScore?.toFixed(1) || '0',
    candidate.totalScore?.toFixed(1) || '0',
    candidate.recommendationForHiring || 'N/A',
    (candidate.skills || []).join('; '),
    candidate.experience || 'Not specified',
    candidate.location || 'Not specified',
    candidate.appliedDate || '',
    candidate.status || '',
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Filter candidates by multiple criteria
export interface CandidateFilters {
  searchTerm?: string;
  skills?: string[];
  experienceLevel?: string;
  verificationStatus?: boolean | 'all';
  dateRange?: { start: Date; end: Date } | null;
  status?: string;
  jobId?: string;
  minScore?: number;
  maxScore?: number;
}

export const filterCandidates = (
  candidates: any[],
  filters: CandidateFilters
): any[] => {
  return candidates.filter((candidate) => {
    // Search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch =
        candidate.name?.toLowerCase().includes(searchLower) ||
        candidate.email?.toLowerCase().includes(searchLower) ||
        candidate.position?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Skills filter
    if (filters.skills && filters.skills.length > 0) {
      const candidateSkills = (candidate.skills || []).map((s: string) => s.toLowerCase());
      const hasRequiredSkills = filters.skills.some((skill) =>
        candidateSkills.some((cs: string) => cs.includes(skill.toLowerCase()))
      );
      if (!hasRequiredSkills) return false;
    }

    // Experience level
    if (filters.experienceLevel && filters.experienceLevel !== 'all') {
      const experience = candidate.experience?.toLowerCase() || '';
      if (!experience.includes(filters.experienceLevel.toLowerCase())) return false;
    }

    // Verification status
    if (filters.verificationStatus !== 'all' && filters.verificationStatus !== undefined) {
      if (candidate.skillsVerified !== filters.verificationStatus) return false;
    }

    // Date range
    if (filters.dateRange) {
      const candidateDate = candidate.completedAt?.toDate
        ? candidate.completedAt.toDate()
        : new Date(candidate.completedAt || candidate.appliedDate);

      if (
        candidateDate < filters.dateRange.start ||
        candidateDate > filters.dateRange.end
      ) {
        return false;
      }
    }

    // Status
    if (filters.status && filters.status !== 'all') {
      if (candidate.status !== filters.status) return false;
    }

    // Score range
    if (filters.minScore !== undefined && candidate.totalScore < filters.minScore) {
      return false;
    }
    if (filters.maxScore !== undefined && candidate.totalScore > filters.maxScore) {
      return false;
    }

    return true;
  });
};

// Get experience level from text
export const parseExperienceLevel = (experienceText: string): string => {
  const text = experienceText.toLowerCase();

  if (text.includes('senior') || text.includes('lead') || text.includes('principal')) {
    return 'Senior';
  }
  if (text.includes('mid') || text.includes('intermediate')) {
    return 'Mid-Level';
  }
  if (text.includes('junior') || text.includes('entry')) {
    return 'Junior';
  }
  if (text.includes('intern')) {
    return 'Intern';
  }

  // Parse years
  const yearsMatch = text.match(/(\d+)\s*(year|yr)/i);
  if (yearsMatch) {
    const years = parseInt(yearsMatch[1]);
    if (years >= 5) return 'Senior';
    if (years >= 2) return 'Mid-Level';
    return 'Junior';
  }

  return 'Not specified';
};

// Sort candidates by different criteria
export type SortCriteria =
  | 'totalScore'
  | 'mcqScore'
  | 'interviewScore'
  | 'appliedDate'
  | 'name'
  | 'skillsMatch';

export const sortCandidates = (
  candidates: any[],
  criteria: SortCriteria,
  direction: 'asc' | 'desc' = 'desc'
): any[] => {
  const sorted = [...candidates].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (criteria) {
      case 'totalScore':
        aValue = a.totalScore || 0;
        bValue = b.totalScore || 0;
        break;
      case 'mcqScore':
        aValue = a.mcqScore || 0;
        bValue = b.mcqScore || 0;
        break;
      case 'interviewScore':
        aValue = a.interviewScore || 0;
        bValue = b.interviewScore || 0;
        break;
      case 'appliedDate':
        aValue = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : 0;
        bValue = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : 0;
        break;
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        return direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      case 'skillsMatch':
        aValue = a.skillsMatchPercentage || 0;
        bValue = b.skillsMatchPercentage || 0;
        break;
      default:
        return 0;
    }

    if (direction === 'asc') {
      return aValue - bValue;
    }
    return bValue - aValue;
  });

  return sorted;
};
